/** Estado da resposta em andamento, dirigido pelos eventos do fluxo SSE.
 *
 *  Reducer puro, separado do hook que abre a conexão, para o contrato dos
 *  eventos ficar provado em teste sem rede. Uma resposta atravessa `aguardando`,
 *  `fluindo` e termina em `concluido`, `erro` ou `parado`. Erro e parada mantêm
 *  o texto que já chegou: parar preserva. */
import type { BlocoSse } from './sse'
import type { ConsultaDaResposta, EtapaDaResposta, EventoFluxo, UsoDoTurno } from './api'

export type StatusDoFluxo = 'ocioso' | 'aguardando' | 'fluindo' | 'concluido' | 'erro' | 'parado'

export interface ErroDoFluxo {
  code: string
  mensagem: string
  /** Só no teto diário: quando o campo volta a aceitar perguntas. */
  voltaEm?: string
}

export interface EstadoDoFluxo {
  status: StatusDoFluxo
  /** uuid da mensagem do assistente, que chega no `inicio`. */
  mensagemUuid: string | null
  texto: string
  etapas: EtapaDaResposta[]
  consultas: ConsultaDaResposta[]
  titulo: string | null
  erro: ErroDoFluxo | null
  uso: UsoDoTurno | null
}

export type AcaoDoFluxo =
  | { tipo: 'abrir' }
  | { tipo: 'evento'; evento: EventoFluxo }
  | { tipo: 'falha'; erro: ErroDoFluxo }
  | { tipo: 'parar' }
  /** A conexão fechou sem `fim`: só vira erro se a resposta ainda estava aberta. */
  | { tipo: 'encerrado' }
  | { tipo: 'limpar' }

export const ESTADO_INICIAL: EstadoDoFluxo = {
  status: 'ocioso',
  mensagemUuid: null,
  texto: '',
  etapas: [],
  consultas: [],
  titulo: null,
  erro: null,
  uso: null,
}

const TERMINAIS: StatusDoFluxo[] = ['concluido', 'erro', 'parado']

export const fluxoTerminou = (status: StatusDoFluxo) => TERMINAIS.includes(status)
export const fluxoEmAndamento = (status: StatusDoFluxo) => status === 'aguardando' || status === 'fluindo'

/** Etapa que chega de novo com o mesmo id troca de estado no lugar; nova entra no fim. */
function aplicarEtapa(etapas: EtapaDaResposta[], etapa: EtapaDaResposta): EtapaDaResposta[] {
  const indice = etapas.findIndex((e) => e.id === etapa.id)
  if (indice === -1) return [...etapas, etapa]
  const proximas = etapas.slice()
  proximas[indice] = { ...etapas[indice], ...etapa }
  return proximas
}

function concluirEtapas(etapas: EtapaDaResposta[]): EtapaDaResposta[] {
  return etapas.map((e) => (e.estado === 'concluida' ? e : { ...e, estado: 'concluida' as const }))
}

export function fluxoReducer(estado: EstadoDoFluxo, acao: AcaoDoFluxo): EstadoDoFluxo {
  switch (acao.tipo) {
    case 'abrir':
      return { ...ESTADO_INICIAL, status: 'aguardando' }
    case 'limpar':
      return ESTADO_INICIAL
    case 'falha':
      if (fluxoTerminou(estado.status)) return estado
      return { ...estado, status: 'erro', erro: acao.erro, etapas: concluirEtapas(estado.etapas) }
    case 'parar':
      if (fluxoTerminou(estado.status) || estado.status === 'ocioso') return estado
      return { ...estado, status: 'parado', etapas: concluirEtapas(estado.etapas) }
    case 'encerrado':
      if (!fluxoEmAndamento(estado.status)) return estado
      return {
        ...estado,
        status: 'erro',
        erro: { code: 'FLUXO_INTERROMPIDO', mensagem: '' },
        etapas: concluirEtapas(estado.etapas),
      }
    case 'evento': {
      if (fluxoTerminou(estado.status)) return estado
      const { evento } = acao
      switch (evento.nome) {
        case 'inicio':
          return { ...estado, status: 'fluindo', mensagemUuid: evento.dados.uuid }
        case 'etapa':
          return { ...estado, status: 'fluindo', etapas: aplicarEtapa(estado.etapas, evento.dados) }
        case 'sql':
          return { ...estado, status: 'fluindo', consultas: [...estado.consultas, evento.dados] }
        case 'texto':
          return { ...estado, status: 'fluindo', texto: estado.texto + evento.dados.trecho }
        case 'titulo':
          return { ...estado, titulo: evento.dados.titulo }
        case 'fim':
          return { ...estado, status: 'concluido', uso: evento.dados, etapas: concluirEtapas(estado.etapas) }
        case 'erro':
          return {
            ...estado,
            status: 'erro',
            erro: { code: evento.dados.code, mensagem: evento.dados.mensagem },
            etapas: concluirEtapas(estado.etapas),
          }
        default:
          return estado
      }
    }
    default:
      return estado
  }
}

type Registro = Record<string, unknown>

const texto = (r: Registro, ...chaves: string[]): string => {
  for (const chave of chaves) {
    const v = r[chave]
    if (typeof v === 'string') return v
  }
  return ''
}
const numero = (r: Registro, ...chaves: string[]): number => {
  for (const chave of chaves) {
    const v = r[chave]
    if (typeof v === 'number') return v
  }
  return 0
}

/** Bloco SSE cru → evento tipado. Os nomes de campo são os do contrato; os
 *  sinônimos aceitos aqui existem só para um desencontro de grafia entre os dois
 *  lados não derrubar o painel na primeira integração. Bloco desconhecido ou
 *  dado que não é JSON devolve null e é ignorado. */
export function interpretarEvento(bloco: BlocoSse): EventoFluxo | null {
  let dados: Registro
  try {
    const bruto: unknown = bloco.dados ? JSON.parse(bloco.dados) : {}
    dados = bruto && typeof bruto === 'object' ? (bruto as Registro) : {}
  } catch {
    return null
  }
  switch (bloco.evento) {
    case 'inicio':
      return { nome: 'inicio', dados: { uuid: texto(dados, 'uuid', 'mensagem_uuid') } }
    case 'etapa': {
      const estado = texto(dados, 'estado') === 'concluida' ? 'concluida' : 'andamento'
      return { nome: 'etapa', dados: { id: texto(dados, 'id'), rotulo: texto(dados, 'rotulo', 'label'), estado } }
    }
    case 'sql':
      return {
        nome: 'sql',
        dados: { sql: texto(dados, 'sql'), objetivo: texto(dados, 'objetivo'), linhas: numero(dados, 'linhas', 'linhas_lidas') },
      }
    case 'texto':
      return { nome: 'texto', dados: { trecho: texto(dados, 'trecho', 'texto', 'delta') } }
    case 'titulo':
      return { nome: 'titulo', dados: { titulo: texto(dados, 'titulo') } }
    case 'fim':
      return {
        nome: 'fim',
        dados: {
          tokens_entrada: numero(dados, 'tokens_entrada'),
          tokens_cache: numero(dados, 'tokens_cache'),
          tokens_saida: numero(dados, 'tokens_saida'),
          duracao_ms: numero(dados, 'duracao_ms'),
        },
      }
    case 'erro':
      return {
        nome: 'erro',
        dados: { code: texto(dados, 'code', 'codigo') || 'INTERNAL_ERROR', mensagem: texto(dados, 'mensagem', 'error', 'frase') },
      }
    default:
      return null
  }
}
